import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Sparkles, 
  Loader2, 
  Plus, 
  Clock, 
  User,
  Calendar,
  CheckCircle2,
  Lightbulb,
  ListTodo,
  Zap,
  Tag,
  Code2,
  Palette,
  Eye,
  FileText,
  ClipboardList
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useActivityTracking } from '@/hooks/useActivityTracking';
import { useLocalization } from '@/hooks/useLocalization';

export interface TaskSuggestion {
  id: number;
  title: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  category: string;
  estimatedEffort: string;
  deadline: string | null;
  assignee: string | null;
  reason: string;
}

interface SmartTaskSuggestionsProps {
  content: string;
  contentType: 'email' | 'meeting';
  sender?: string;
  subject?: string;
  emailId?: string;
  onTaskAdded?: (task: TaskSuggestion) => void;
  compact?: boolean;
}

const SmartTaskSuggestions = ({
  content,
  contentType,
  sender,
  subject,
  emailId,
  onTaskAdded,
  compact = false
}: SmartTaskSuggestionsProps) => {
  const { tx, t } = useLocalization();
  const [suggestions, setSuggestions] = useState<TaskSuggestion[]>([]);
  const [summary, setSummary] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [addedTasks, setAddedTasks] = useState<Set<number>>(new Set());
  const [addingTaskId, setAddingTaskId] = useState<number | null>(null);
  const { trackAction } = useActivityTracking();
  const { toast } = useToast();

  const generateSuggestions = async () => {
    setIsLoading(true);
    setSuggestions([]);
    setAddedTasks(new Set());

    try {
      const { data, error } = await supabase.functions.invoke('suggest-tasks', {
        body: {
          content,
          contentType,
          sender,
          subject,
        },
      });

      if (error) throw error;

      if (data.error) {
        throw new Error(data.error);
      }

      setSuggestions(data.suggestions || []);
      setSummary(data.summary || '');
      
      if (data.suggestions?.length > 0) {
        toast({ title: t('Suggestions ready'), description: t('Found {0} task suggestions', { 0: data.suggestions.length }) });
      } else {
        toast({ title: t('No tasks found'), description: t('No actionable tasks found in content') });
      }
    } catch (error) {
      console.error('Error generating suggestions:', error);
      toast({ title: t('Error'), description: t('Failed to generate suggestions'), variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddTask = async (task: TaskSuggestion) => {
    setAddingTaskId(task.id);
    try {
      // Writes into action_items (Action Hub), not tasks_sync — since Task
      // Board was removed, tasks_sync rows created here would be invisible
      // to users (no UI reads manually-created tasks_sync rows anymore).
      // status: 'suggested' — clicking "Add Task" proposes the AI
      // suggestion into Action Hub's review queue; it still needs explicit
      // approval there, matching Action Hub's default 'suggested' tab.
      // assignee/deadline don't map cleanly to action_items.assignee_id
      // (an auth.users FK, not free text), so they're kept in raw_payload
      // alongside reason/category rather than attempting a lossy FK match.
      const { error } = await supabase.from('action_items').insert({
        source: contentType === 'email' ? 'email' : 'meeting',
        title: task.title,
        description: task.description,
        status: 'suggested',
        source_email_id: contentType === 'email' ? emailId ?? null : null,
        raw_payload: {
          reason: task.reason,
          category: task.category,
          estimatedEffort: task.estimatedEffort,
          assignee: task.assignee,
          deadline: task.deadline,
        },
      });

      if (error) throw error;

      trackAction({
        actionType: 'task_created',
        actionDetails: { title: task.title, source: 'ai_suggestion', contentType },
      });

      setAddedTasks(prev => new Set(prev).add(task.id));
      onTaskAdded?.(task);
      toast({ title: t('Task added'), description: t('Task "{0}" added to Action Hub', { 0: task.title }) });
    } catch (error) {
      console.error('Error adding task to Action Hub:', error);
      toast({ title: t('Error'), description: t('Failed to add task to Action Hub'), variant: 'destructive' });
    } finally {
      setAddingTaskId(null);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-destructive/20 text-destructive';
      case 'medium': return 'bg-amber-500/20 text-amber-600 dark:text-amber-400';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'development': return <Code2 className="h-3.5 w-3.5" />;
      case 'design': return <Palette className="h-3.5 w-3.5" />;
      case 'review': return <Eye className="h-3.5 w-3.5" />;
      case 'meeting': return <Calendar className="h-3.5 w-3.5" />;
      case 'documentation': return <FileText className="h-3.5 w-3.5" />;
      default: return <ClipboardList className="h-3.5 w-3.5" />;
    }
  };

  if (compact) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Lightbulb className="h-4 w-4 text-primary" />
            <span>{t('Smart Task Suggestions')}</span>
          </div>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={generateSuggestions}
            disabled={isLoading}
            className="gap-1"
          >
            {isLoading ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Sparkles className="h-3 w-3" />
            )}
            {suggestions.length > 0 ? t('Refresh') : t('Suggest Tasks')}
          </Button>
        </div>

        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            
            {t('Analyzing content...')}
          </div>
        )}

        {suggestions.length > 0 && (
          <div className="space-y-2">
            {suggestions.slice(0, 3).map((task) => (
              <div 
                key={task.id} 
                className={cn(
                  "flex items-start justify-between gap-2 p-2 rounded-lg border bg-card/50",
                  addedTasks.has(task.id) && "opacity-60"
                )}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="text-muted-foreground">{getCategoryIcon(task.category)}</span>
                    <span className="text-sm font-medium truncate">{tx(task.title)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className={cn("text-[10px] px-1.5 py-0", getPriorityColor(task.priority))}>
                      {tx(task.priority)}
                    </Badge>
                    {task.estimatedEffort && (
                      <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                        <Clock className="h-2.5 w-2.5" />
                        {task.estimatedEffort}
                      </span>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0 flex-shrink-0"
                  onClick={() => handleAddTask(task)}
                  disabled={addedTasks.has(task.id) || addingTaskId === task.id}
                >
                  {addedTasks.has(task.id) ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  ) : addingTaskId === task.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                </Button>
              </div>
            ))}
            {suggestions.length > 3 && (
              <p className="text-xs text-muted-foreground text-center">
                +{suggestions.length - 3}  {t('more suggestions')}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Lightbulb className="h-4 w-4 text-primary" />
            
            {t('Smart Task Suggestions')}
          </CardTitle>
          <Button 
            size="sm" 
            onClick={generateSuggestions}
            disabled={isLoading}
            className="gap-1"
          >
            {isLoading ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Sparkles className="h-3 w-3" />
            )}
            {suggestions.length > 0 ? t('Regenerate') : t('Analyze & Suggest')}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {!suggestions.length && !isLoading && (
          <div className="text-center py-6">
            <div className="p-3 rounded-full bg-primary/10 w-fit mx-auto mb-3">
              <ListTodo className="h-6 w-6 text-primary" />
            </div>
            <p className="text-sm text-muted-foreground">
              
              {t('Click "Analyze & Suggest" to extract actionable tasks from this')} {contentType}.
            </p>
          </div>
        )}

        {isLoading && (
          <div className="flex flex-col items-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-primary mb-2" />
            <p className="text-sm text-muted-foreground">{t('Analyzing')} {contentType}  {t('content...')}</p>
          </div>
        )}

        {suggestions.length > 0 && (
          <div className="space-y-4">
            {summary && (
              <div className="p-3 rounded-lg bg-primary/5 border border-primary/20 text-sm text-muted-foreground">
                {summary}
              </div>
            )}

            <div className="space-y-3">
              {suggestions.map((task) => (
                <div 
                  key={task.id} 
                  className={cn(
                    "p-3 rounded-lg border bg-card transition-all",
                    addedTasks.has(task.id) && "opacity-60 bg-muted/30"
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-muted-foreground">{getCategoryIcon(task.category)}</span>
                        <span className="font-medium text-sm">{tx(task.title)}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mb-2">{tx(task.description)}</p>
                      
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="secondary" className={cn("text-xs", getPriorityColor(task.priority))}>
                          <Zap className="h-2.5 w-2.5 me-1" />
                          {tx(task.priority)}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          <Tag className="h-2.5 w-2.5 me-1" />
                          {task.category}
                        </Badge>
                        {task.estimatedEffort && (
                          <Badge variant="outline" className="text-xs">
                            <Clock className="h-2.5 w-2.5 me-1" />
                            {task.estimatedEffort}
                          </Badge>
                        )}
                        {task.deadline && (
                          <Badge variant="outline" className="text-xs">
                            <Calendar className="h-2.5 w-2.5 me-1" />
                            {task.deadline}
                          </Badge>
                        )}
                        {task.assignee && (
                          <Badge variant="outline" className="text-xs">
                            <User className="h-2.5 w-2.5 me-1" />
                            {task.assignee}
                          </Badge>
                        )}
                      </div>

                      {task.reason && (
                        <p className="text-[10px] text-muted-foreground mt-2 italic flex items-center gap-1">
                          <Lightbulb className="h-3 w-3 shrink-0" /> {task.reason}
                        </p>
                      )}
                    </div>

                    <Button
                      size="sm"
                      variant={addedTasks.has(task.id) ? "ghost" : "default"}
                      onClick={() => handleAddTask(task)}
                      disabled={addedTasks.has(task.id) || addingTaskId === task.id}
                      className="gap-1 flex-shrink-0"
                    >
                      {addedTasks.has(task.id) ? (
                        <>
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                          
                          {t('Added')}
                        </>
                      ) : addingTaskId === task.id ? (
                        <>
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          
                          {t('Adding...')}
                        </>
                      ) : (
                        <>
                          <Plus className="h-3.5 w-3.5" />
                          
                          {t('Add Task')}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SmartTaskSuggestions;
